// react
import React from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';
import { resolveIcon } from '@/components/atoms/Icon/resolveIcon';

// mux
import MuxPlayer from '@mux/mux-player-react';

// mui
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface FormIntroItem {
  icon: string;
  title?: string;
  text: string;
}

export interface FormIntroPageProps {
  onBack: () => void;
  title: string;
  description?: string;
  items: FormIntroItem[];
  itemsHeading?: string;
  infoTitle?: string;
  infoText?: string;
  onStart: () => void;
  startLabel?: string;
  contentSlot?: React.ReactNode;
  additionalActions?: { label: string; onClick: () => void }[];
  muxPlaybackId?: string;
  muxThumbnailTime?: number;
}

/**
 * FormIntroPage is an introduction page shown before a user starts a form.
 * It uses ModalPageTemplate + PageTemplate for consistent navigation.
 */
export const FormIntroPage = ({
  onBack,
  title,
  description,
  items,
  itemsHeading,
  infoTitle = 'Did you know?',
  infoText,
  onStart,
  startLabel = 'Continue',
  contentSlot,
  additionalActions,
  muxPlaybackId,
  muxThumbnailTime,
}: FormIntroPageProps) => {
  const theme = useTheme();

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            spacing={2}
            sx={{
              maxWidth: 'sm',
              mx: 'auto',
            }}
          >
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={onStart}
            >
              {startLabel}
            </Button>
            {additionalActions?.map((action, index) => (
              <Button
                key={index}
                variant='outlined'
                size='large'
                fullWidth
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        </Box>
      }
    >
      <PageTemplate
        maxWidth='sm'
        headerSlot={<PageHeader title={title} description={description} />}
        contentSlot={
          <Stack spacing={2}>
            {muxPlaybackId && (
              <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <MuxPlayer
                  playbackId={muxPlaybackId}
                  thumbnailTime={muxThumbnailTime}
                  style={{ aspectRatio: '16 / 9', display: 'block' }}
                />
              </Box>
            )}

            {contentSlot}

            {items.length > 0 && (
              <Card elevation={0}>
                <CardContent>
                  <Stack spacing={2}>
                    {itemsHeading && (
                      <Typography variant='h2'>{itemsHeading}</Typography>
                    )}
                    <Stack spacing={3}>
                      {items.map((item, index) => (
                        <Stack
                          key={index}
                          direction='row'
                          spacing={1}
                          sx={{
                            alignItems: item.title ? 'flex-start' : 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 40,
                              minWidth: 40,
                              color: theme.palette.primary.main,
                            }}
                          >
                            <Icon icon={resolveIcon(item.icon)} fontSize={24} />
                          </Box>
                          {item.title ? (
                            <Stack spacing={0.5}>
                              <Typography variant='subtitle1'>
                                {item.title}
                              </Typography>
                              <Typography variant='body2'>
                                {item.text}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant='body2'>{item.text}</Typography>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {infoText && (
              <Alert severity='info'>
                <AlertTitle>{infoTitle}</AlertTitle>
                {infoText}
              </Alert>
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

FormIntroPage.displayName = 'FormIntroPage';
export default FormIntroPage;
